import { useAdminApi } from '@hooks/useAdminApi';
import { Box, Stack, Typography } from '@mui/material';
import { PricingType } from '@protos/pricing';
import { Product } from '@protos/product';
import { LadderLimit, User } from '@protos/user';
import { SearchGroupedProducts } from '@shared/components/SearchGroupedProducts';
import { TextField } from '@shared/components/TextField';
import { useEffect, useMemo, useState } from 'react';
import { useAsync } from 'react-use';
import { getCurrentUser } from '../utils';
import { BaseEditDialog } from './BaseEditDialog';
import { StyledIconButton } from './StyledIconButton';

interface EditLadderLimitsDialogProps {
  user: User;
  isOpen: boolean;
  onClose: () => void;
  onUserUpdated: (user: User) => void;
}

const NumberRegex = new RegExp('^[0-9]*$');

export const EditLadderLimitsDialog: React.FC<EditLadderLimitsDialogProps> = ({ user, isOpen, onClose, onUserUpdated }) => {
  const { apiClient } = useAdminApi();
  const [ladderLimits, setLadderLimits] = useState<LadderLimit[]>([...user.ladder_limits]);
  const currentUser = getCurrentUser(user);

  const products = useAsync(async () => {
    const response = await apiClient?.productsLoader({ pricing_type: PricingType.Trader }).loadAllData();
    return response;
  }, [apiClient]);

  const allProductGroups = useMemo(() => {
    const groups: { [key: string]: Product[] } = {};
    if (products.loading || !products.value) return groups;

    products.value.forEach(product => {
      if (!groups[product.product_group]) {
        groups[product.product_group] = [];
      }
      groups[product.product_group].push(product);
    });
    return groups;
  }, [products]);

  const selectedProducts = useMemo(() => {
    if (products.loading || !products.value) return [];

    return products.value.filter(p => ladderLimits.map(t => t.symbol).includes(p.symbol));
  }, [products, ladderLimits]);

  const onSave = async () => {
    if (apiClient && user) {
      await apiClient.updateUserLimits(user.id, { ...currentUser, ladder_limits: ladderLimits }).then(data => {
        setLadderLimits(data.ladder_limits);
        onUserUpdated(data);
      });
    }
  };

  const onLadderLimitAdd = (product: Product) => {
    setLadderLimits([...ladderLimits, { symbol: product.symbol, limit: 0 }]);
  };

  const onLadderLimitRemove = (ladderId: number) => {
    const updatedLadders = [...ladderLimits];
    updatedLadders.splice(ladderId, 1);
    setLadderLimits(updatedLadders);
  };

  const onCheckboxChange = (product: Product) => {
    const ladderId = ladderLimits.findIndex(limit => limit.symbol === product.symbol);
    if (ladderId === -1) onLadderLimitAdd(product);
    else onLadderLimitRemove(ladderId);
  };

  const onLadderLimitUpdate = (ladderId: number, updatedValue: LadderLimit) => {
    const updatedLadders = [...ladderLimits];
    updatedLadders.splice(ladderId, 1, updatedValue);
    setLadderLimits(updatedLadders);
  };

  useEffect(() => {
    setLadderLimits(user.ladder_limits);
  }, [user, isOpen]);

  if (products.loading || !products.value) return null;

  return (
    <BaseEditDialog
      title="Edit Ladder Limits"
      isOpen={isOpen}
      onClose={onClose}
      clearErrorOn={[ladderLimits]}
      defaultErrorMessage="There was an error updating the ladder limits"
      onSave={onSave}
    >
      <Box display="flex" marginRight={2} gap={1}>
        <Stack direction="column" alignItems="flex-start">
          {ladderLimits.map((ladderLimit, id) => (
            <Stack key={`ladder_limit_${ladderLimit.symbol}`} direction="row" alignItems="center">
              <Typography variant="h6" minWidth="120px">
                {ladderLimit.symbol}
              </Typography>
              <div style={{ margin: '0 15px' }}>
                <TextField
                  label="Limit"
                  value={ladderLimit.limit.toString()}
                  error={ladderLimit.limit === 0}
                  sx={{ width: '100px' }}
                  onChange={(value: string) => {
                    if (NumberRegex.test(value)) onLadderLimitUpdate(id, { ...ladderLimit, limit: Number(value) });
                  }}
                />
              </div>
              <StyledIconButton onClick={() => onLadderLimitRemove(id)} size={'small'} style={{ marginLeft: '5px' }}>
                <i className={'ri-delete-bin-line'} />
              </StyledIconButton>
            </Stack>
          ))}

          <Stack marginTop={2} width="550px">
            <SearchGroupedProducts
              allProductGroups={allProductGroups}
              allSelectedProducts={selectedProducts}
              selectedUserProducts={selectedProducts}
              onProductClick={onCheckboxChange}
              getProductCount={() => selectedProducts.length}
              isSelectionDisabled={() => false}
              maxProductsLimit={products.value.length}
            />
          </Stack>
        </Stack>
      </Box>
    </BaseEditDialog>
  );
};
